/**
 * slide-templates — the deterministic layout engine. PURE: zero Office.js
 * imports, fully unit-testable. Turns a declarative slide spec (template name +
 * content + palette) into ShapePlan[]s whose geometry is computed HERE, in
 * code — not improvised by the model. This is the misalignment fix: the zone
 * tables from the design-recipe fragments (split-hero 56529f6e, cover c0bfbad0,
 * three-up 4ff171ad, stat-band 789bbe54) live in these constants.
 *
 * Rendering happens in tools/layout.ts (compose_slide) via shape-builders.
 */

import type { FontSpec, Rect, ShapePlan } from "./shape-builders";

// ---------------------------------------------------------------------------
// Canvas & palette
// ---------------------------------------------------------------------------

/**
 * Widescreen 16:9 slide in points. Office.js exposes no slide-size API, so
 * this is an assumption — compose_slide surfaces a warning when existing
 * shapes suggest a 4:3/custom deck.
 */
export const CANVAS = { width: 960, height: 540 } as const;

export interface Palette {
  /** Slide background field. */
  background: string;
  /** Panel/surface colour slightly off the field (image placeholders, cards). */
  surface: string;
  /** Primary text colour (must contrast with background). */
  text: string;
  /** Secondary/muted text colour. */
  muted: string;
  /** The single accent (eyebrows, stats, dividers) — 3-colour discipline. */
  accent: string;
}

export const NAMED_PALETTES: Record<string, Palette> = {
  // Deep field + cream + amber (the BEARS/golden-example palette family).
  dark: {
    background: "#15392B",
    surface: "#1E4A38",
    text: "#F5EFE1",
    muted: "#C8BFA8",
    accent: "#E8A33D",
  },
  // Clean white with near-black text and a restrained blue accent.
  light: {
    background: "#FFFFFF",
    surface: "#F2F4F7",
    text: "#1A1A24",
    muted: "#5C6470",
    accent: "#0F6CBD",
  },
  // Usable brand purple family.
  brand: {
    background: "#241B3A",
    surface: "#2F2450",
    text: "#F4F1FA",
    muted: "#BBAFD6",
    accent: "#A78BFA",
  },
};

export interface Fonts {
  heading: string;
  body: string;
}

export const DEFAULT_FONTS: Fonts = { heading: "Segoe UI", body: "Segoe UI" };

// ---------------------------------------------------------------------------
// Spec & result types
// ---------------------------------------------------------------------------

export const TEMPLATE_NAMES = [
  "cover",
  "split-hero",
  "three-up",
  "stat-band",
  "title-bullets",
  "quote",
] as const;
export type TemplateName = (typeof TEMPLATE_NAMES)[number];

export interface SlideContent {
  eyebrow?: string;
  title?: string;
  subtitle?: string;
  footer?: string;
  bullets?: string[];
  stats?: Array<{ value: string; label: string }>;
  items?: Array<{ icon?: string; title: string; body?: string }>;
  quote?: string;
  attribution?: string;
  /** Whether an image will be supplied (the tool patches in the base64). */
  hasImage?: boolean;
}

/** A ShapePlan tagged with its slot name so callers can map created ids. */
export type SlotPlan = ShapePlan & { slot: string };

export interface TemplateResult {
  plans: SlotPlan[];
  warnings: string[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Auto-scale a title font so long text doesn't spill its zone (recipe rule →
 * code). Approximation: average glyph width ≈ 0.52 × fontSize for bold
 * sans-serif; shrink until the text fits maxLines in the zone, floored.
 */
export function scaleTitleFont(
  text: string,
  zone: Rect,
  baseSize: number,
  minSize: number,
  maxLines = 2
): { size: number; warning?: string } {
  let size = baseSize;
  while (size > minSize) {
    const charsPerLine = Math.floor(zone.width / (0.52 * size));
    const linesNeeded = Math.ceil(text.length / Math.max(charsPerLine, 1));
    const lineHeight = size * 1.15;
    if (linesNeeded <= maxLines && linesNeeded * lineHeight <= zone.height) return { size };
    size -= 2;
  }
  const charsPerLine = Math.floor(zone.width / (0.52 * minSize));
  const linesNeeded = Math.ceil(text.length / Math.max(charsPerLine, 1));
  return {
    size: minSize,
    warning:
      linesNeeded * minSize * 1.15 > zone.height
        ? `Title is very long (${text.length} chars) — scaled to the minimum ${minSize}pt and may still wrap beyond its zone. Consider a shorter assertion title.`
        : undefined,
  };
}

/**
 * Strip the markdown the model habitually writes (**bold**, __underline__,
 * `code`) — PowerPoint renders it literally. The engine's font specs carry the
 * emphasis instead.
 */
export function cleanText(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, "$1")
    .replace(/__(.+?)__/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/^\s*[-•]\s+/, "")
    .trim();
}

function textbox(
  slot: string,
  rect: Rect,
  text: string,
  font: FontSpec,
  extras: Partial<Extract<ShapePlan, { kind: "textbox" }>> = {}
): SlotPlan {
  // NO autofit by default: PowerPoint's text-to-fit-shape GROWS short text to
  // fill its box, which destroyed the deliberate type scale (a short stat like
  // "5 Days" ballooned past its siblings and broke the shared baseline —
  // found live). The engine computes sizes; boxes must respect them.
  return { slot, kind: "textbox", rect, text: cleanText(text), font, ...extras };
}

/**
 * One shared font size for a row of stat values: large as possible while the
 * LONGEST value still fits its column on a single line (avg bold glyph ≈
 * 0.55 × size). A fixed size wraps long values ("1 Month" at 72pt in a 240pt
 * column) and the overflow climbs into the title — found live.
 */
export function fitStatSize(
  values: string[],
  columnWidth: number,
  maxSize: number,
  minSize: number
): number {
  if (values.length === 0) return maxSize;
  const fitted = values.map((v) =>
    Math.floor(columnWidth / (0.55 * Math.max(v.length, 1)))
  );
  return Math.max(minSize, Math.min(maxSize, ...fitted));
}

/** Evenly-spaced column x-positions per the recipes' zone tables. */
export function columnLayout(count: number, kind: "three-up" | "stat-band"): { xs: number[]; width: number } {
  if (kind === "three-up") {
    if (count <= 2) return { xs: [180, 560], width: 220 };
    if (count === 3) return { xs: [80, 380, 680], width: 220 };
    return { xs: [60, 287, 514, 741], width: 185 }; // 4
  }
  // stat-band
  if (count <= 2) return { xs: [180, 560], width: 240 };
  if (count === 3) return { xs: [80, 380, 680], width: 240 };
  return { xs: [60, 285, 510, 735], width: 200 }; // 4
}

// ---------------------------------------------------------------------------
// Templates (geometry from the recipe fragments)
// ---------------------------------------------------------------------------

type TemplateFn = (content: SlideContent, palette: Palette, fonts: Fonts, warnings: string[]) => SlotPlan[];

const cover: TemplateFn = (content, palette, fonts, warnings) => {
  const plans: SlotPlan[] = [];
  const title = content.title ?? "";
  if (content.eyebrow) {
    plans.push(
      textbox("eyebrow", { left: 80, top: 150, width: 500, height: 30 }, content.eyebrow.toUpperCase(), {
        name: fonts.heading, size: 14, color: palette.accent, bold: true,
      })
    );
  }
  const titleZone = { left: 80, top: 196, width: 700, height: 150 };
  const scaled = scaleTitleFont(title, titleZone, 54, 32);
  if (scaled.warning) warnings.push(scaled.warning);
  plans.push(
    textbox("title", titleZone, title, { name: fonts.heading, size: scaled.size, color: palette.text, bold: true })
  );
  // Accent divider — a real filled rectangle now that add_shape/fill exist.
  plans.push({
    slot: "divider",
    kind: "shape",
    shapeType: "rectangle",
    rect: { left: 80, top: 356, width: 120, height: 6 },
    fill: { color: palette.accent },
  });
  if (content.subtitle) {
    plans.push(
      textbox("subtitle", { left: 80, top: 376, width: 640, height: 70 }, content.subtitle, {
        name: fonts.body, size: 22, color: palette.muted,
      })
    );
  }
  if (content.footer) {
    plans.push(
      textbox("footer", { left: 80, top: 488, width: 700, height: 24 }, content.footer, {
        name: fonts.body, size: 12, color: palette.muted,
      })
    );
  }
  return plans;
};

const splitHero: TemplateFn = (content, palette, fonts, warnings) => {
  const plans: SlotPlan[] = [];
  const title = content.title ?? "";

  // Right image panel, full-bleed 500→960. Real image when provided; otherwise
  // a surface-coloured panel with a label so the geometry stays drop-in.
  const imageZone = { left: 500, top: 0, width: 460, height: 540 };
  if (content.hasImage) {
    plans.push({ slot: "image", kind: "image", rect: imageZone, base64: "" });
  } else {
    plans.push({
      slot: "image_placeholder",
      kind: "shape",
      shapeType: "rectangle",
      rect: imageZone,
      fill: { color: palette.surface },
    });
    plans.push(
      textbox("image_label", { left: 560, top: 250, width: 340, height: 40 }, "🖼 image", {
        name: fonts.body, size: 16, color: palette.muted,
      }, { align: "center" })
    );
    // Actionable, not just descriptive — the model reads warnings in the tool
    // result and should close the loop instead of shipping an empty panel.
    warnings.push(
      "split-hero image panel is a PLACEHOLDER (no image was provided). Do not leave it empty: " +
        "generate a fitting image, then delete the 'image_placeholder' and 'image_label' shapes " +
        "and call insert_image { slideIndex: <this slide>, left: 500, top: 0, width: 460, " +
        "height: 540, fit: \"stretch\", useGeneratedImage: true } — or re-compose this slide " +
        "with content.useGeneratedImage after generating."
    );
  }

  if (content.eyebrow) {
    plans.push(
      textbox("eyebrow", { left: 60, top: 64, width: 380, height: 28 }, content.eyebrow.toUpperCase(), {
        name: fonts.heading, size: 14, color: palette.accent, bold: true,
      })
    );
  }
  const titleZone = { left: 60, top: 130, width: 420, height: 130 };
  const scaled = scaleTitleFont(title, titleZone, 60, 34);
  if (scaled.warning) warnings.push(scaled.warning);
  plans.push(
    textbox("title", titleZone, title, { name: fonts.heading, size: scaled.size, color: palette.text, bold: true })
  );
  if (content.subtitle) {
    plans.push(
      textbox("subtitle", { left: 60, top: 268, width: 410, height: 80 }, content.subtitle, {
        name: fonts.body, size: 20, color: palette.text,
      })
    );
  }

  const stats = (content.stats ?? []).slice(0, 3);
  if ((content.stats ?? []).length > 3) warnings.push("split-hero shows at most 3 stats — extra stats dropped.");
  const heroStatSize = fitStatSize(stats.map((s) => cleanText(s.value)), 130, 36, 16);
  stats.forEach((stat, i) => {
    const x = 60 + i * 135;
    plans.push(
      textbox(`stat_${i}_value`, { left: x, top: 372, width: 130, height: 60 }, stat.value, {
        name: fonts.heading, size: heroStatSize, color: palette.accent, bold: true,
      }, { verticalAlign: "bottom", wordWrap: false })
    );
    plans.push(
      textbox(`stat_${i}_label`, { left: x, top: 432, width: 130, height: 36 }, stat.label, {
        name: fonts.body, size: 12, color: palette.muted,
      })
    );
  });

  const callouts = (content.bullets ?? []).slice(0, 2);
  if ((content.bullets ?? []).length > 2) warnings.push("split-hero shows at most 2 callouts — extras dropped.");
  callouts.forEach((callout, i) => {
    plans.push(
      textbox(`callout_${i}`, { left: 60, top: 478 + i * 26, width: 420, height: 26 }, callout, {
        name: fonts.body, size: 14, color: palette.text,
      })
    );
  });

  return plans;
};

const threeUp: TemplateFn = (content, palette, fonts, warnings) => {
  const plans: SlotPlan[] = [];
  const items = (content.items ?? []).slice(0, 4);
  if ((content.items ?? []).length > 4) warnings.push("three-up shows at most 4 items — extras dropped.");
  if (items.length < 2) warnings.push("three-up works best with 2–4 items.");

  if (content.eyebrow) {
    plans.push(
      textbox("eyebrow", { left: 60, top: 56, width: 500, height: 28 }, content.eyebrow.toUpperCase(), {
        name: fonts.heading, size: 14, color: palette.accent, bold: true,
      })
    );
  }
  const titleZone = { left: 60, top: 92, width: 840, height: 70 };
  const scaled = scaleTitleFont(content.title ?? "", titleZone, 32, 24);
  if (scaled.warning) warnings.push(scaled.warning);
  plans.push(
    textbox("title", titleZone, content.title ?? "", {
      name: fonts.heading, size: scaled.size, color: palette.text, bold: true,
    })
  );

  const { xs, width } = columnLayout(items.length, "three-up");

  // Wrap-aware row layout (live-found overlap: "Secure Authentication" wraps
  // to two 20pt lines but the title box was sized for one, spilling into the
  // body pinned right below). Measure every title with the same glyph
  // estimate scaleTitleFont uses, size the title row for the TALLEST one, and
  // start every body at that shared baseline so columns stay aligned.
  const TITLE_SIZE = 20;
  const estimateLines = (text: string): number => {
    const charsPerLine = Math.floor(width / (0.52 * TITLE_SIZE));
    return Math.max(1, Math.ceil(text.length / Math.max(charsPerLine, 1)));
  };
  const titleLines = Math.min(3, Math.max(...items.map((it) => estimateLines(it.title)), 1));
  const titleTop = 250;
  const titleHeight = Math.ceil(titleLines * TITLE_SIZE * 1.25) + 6;
  const bodyTop = titleTop + titleHeight + 10;

  items.forEach((item, i) => {
    const x = xs[i];
    plans.push(
      textbox(`item_${i}_icon`, { left: x, top: 178, width: 60, height: 60 }, item.icon ?? `0${i + 1}`, {
        name: fonts.heading, size: 40, color: palette.accent, bold: true,
      })
    );
    plans.push(
      textbox(`item_${i}_title`, { left: x, top: titleTop, width, height: titleHeight }, item.title, {
        name: fonts.heading, size: TITLE_SIZE, color: palette.text, bold: true,
      })
    );
    if (item.body) {
      plans.push(
        textbox(`item_${i}_body`, { left: x, top: bodyTop, width, height: Math.min(150, 500 - bodyTop) }, item.body, {
          name: fonts.body, size: 16, color: palette.muted,
        })
      );
    }
  });
  return plans;
};

const statBand: TemplateFn = (content, palette, fonts, warnings) => {
  const plans: SlotPlan[] = [];
  const stats = (content.stats ?? []).slice(0, 4);
  if ((content.stats ?? []).length > 4) warnings.push("stat-band shows at most 4 stats — extras dropped.");
  if (stats.length < 2) warnings.push("stat-band works best with 2–4 stats.");

  if (content.eyebrow) {
    plans.push(
      textbox("eyebrow", { left: 60, top: 70, width: 500, height: 28 }, content.eyebrow.toUpperCase(), {
        name: fonts.heading, size: 14, color: palette.accent, bold: true,
      })
    );
  }
  plans.push(
    textbox("title", { left: 60, top: 108, width: 840, height: 60 }, content.title ?? "", {
      name: fonts.heading, size: 28, color: palette.text, bold: true,
    })
  );

  const { xs, width } = columnLayout(stats.length, "stat-band");
  const statSize = fitStatSize(stats.map((s) => cleanText(s.value)), width, 72, 32);
  if (statSize === 32) {
    warnings.push("A stat value is long — rendered at the minimum 32pt. Shorter figures land harder.");
  }
  stats.forEach((stat, i) => {
    const x = xs[i];
    plans.push(
      textbox(`stat_${i}_value`, { left: x, top: 250, width, height: 90 }, stat.value, {
        name: fonts.heading, size: statSize, color: palette.accent, bold: true,
      }, { verticalAlign: "bottom", wordWrap: false }) // one line, shared baseline
    );
    plans.push(
      textbox(`stat_${i}_label`, { left: x, top: 348, width, height: 60 }, stat.label, {
        name: fonts.body, size: 17, color: palette.muted,
      }, { verticalAlign: "top" })
    );
  });
  return plans;
};

const titleBullets: TemplateFn = (content, palette, fonts, warnings) => {
  const plans: SlotPlan[] = [];
  const bullets = (content.bullets ?? []).slice(0, 6);
  if ((content.bullets ?? []).length > 6) {
    warnings.push("title-bullets shows at most 6 bullets — extras dropped. Consider splitting the slide.");
  }
  const titleZone = { left: 60, top: 60, width: 840, height: 70 };
  const scaled = scaleTitleFont(content.title ?? "", titleZone, 32, 24);
  if (scaled.warning) warnings.push(scaled.warning);
  plans.push(
    textbox("title", titleZone, content.title ?? "", {
      name: fonts.heading, size: scaled.size, color: palette.text, bold: true,
    })
  );
  if (bullets.length > 0) {
    plans.push(
      textbox(
        "bullets",
        { left: 80, top: 170, width: 800, height: Math.min(48 * bullets.length + 24, 340) },
        bullets.map(cleanText).join("\n"),
        { name: fonts.body, size: 18, color: palette.text },
        { bulletList: true, verticalAlign: "top" }
      )
    );
  }
  return plans;
};

const quote: TemplateFn = (content, palette, fonts, warnings) => {
  const plans: SlotPlan[] = [];
  if (!content.quote) warnings.push("quote template without quote content.");
  plans.push({
    slot: "divider",
    kind: "shape",
    shapeType: "rectangle",
    rect: { left: 420, top: 130, width: 120, height: 6 },
    fill: { color: palette.accent },
  });
  plans.push(
    textbox("quote", { left: 120, top: 170, width: 720, height: 180 }, `“${content.quote ?? ""}”`, {
      name: fonts.heading, size: 30, color: palette.text, italic: true,
    }, { align: "center" })
  );
  if (content.attribution) {
    plans.push(
      textbox("attribution", { left: 120, top: 370, width: 720, height: 40 }, `— ${content.attribution}`, {
        name: fonts.body, size: 16, color: palette.muted,
      }, { align: "center" })
    );
  }
  return plans;
};

const TEMPLATES: Record<TemplateName, TemplateFn> = {
  cover,
  "split-hero": splitHero,
  "three-up": threeUp,
  "stat-band": statBand,
  "title-bullets": titleBullets,
  quote,
};

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

export interface TemplateSpec {
  template: TemplateName;
  content: SlideContent;
  palette?: Palette | keyof typeof NAMED_PALETTES | string;
  fonts?: Partial<Fonts>;
}

export function resolvePalette(palette: TemplateSpec["palette"]): Palette {
  if (palette === undefined) return NAMED_PALETTES.dark;
  if (typeof palette === "string") {
    const named = NAMED_PALETTES[palette];
    if (!named) {
      throw new Error(
        `Unknown palette "${palette}". Use one of: ${Object.keys(NAMED_PALETTES).join(", ")}, or pass a palette object.`
      );
    }
    return named;
  }
  return palette;
}

export function computeTemplate(spec: TemplateSpec): TemplateResult {
  const templateFn = TEMPLATES[spec.template];
  if (!templateFn) {
    throw new Error(
      `Unknown template "${String(spec.template)}". Valid templates: ${TEMPLATE_NAMES.join(", ")}.`
    );
  }
  if (!spec.content?.title && spec.template !== "quote") {
    throw new Error(`Template "${spec.template}" requires content.title (an assertion title).`);
  }
  const palette = resolvePalette(spec.palette);
  const fonts: Fonts = { ...DEFAULT_FONTS, ...spec.fonts };
  const warnings: string[] = [];
  const plans = templateFn(spec.content ?? {}, palette, fonts, warnings);
  return { plans, warnings };
}

// ---------------------------------------------------------------------------
// Alignment / distribution math (pure — used by align_shapes/distribute_shapes)
// ---------------------------------------------------------------------------

export type Alignment = "left" | "centerH" | "right" | "top" | "centerV" | "bottom";

/** Align rects against their union bounding box; returns NEW rects in order. */
export function computeAlignment(rects: Rect[], alignment: Alignment): Rect[] {
  if (rects.length === 0) return [];
  const minLeft = Math.min(...rects.map((r) => r.left));
  const maxRight = Math.max(...rects.map((r) => r.left + r.width));
  const minTop = Math.min(...rects.map((r) => r.top));
  const maxBottom = Math.max(...rects.map((r) => r.top + r.height));
  const centerX = (minLeft + maxRight) / 2;
  const centerY = (minTop + maxBottom) / 2;

  return rects.map((r) => {
    switch (alignment) {
      case "left":    return { ...r, left: minLeft };
      case "right":   return { ...r, left: maxRight - r.width };
      case "centerH": return { ...r, left: centerX - r.width / 2 };
      case "top":     return { ...r, top: minTop };
      case "bottom":  return { ...r, top: maxBottom - r.height };
      case "centerV": return { ...r, top: centerY - r.height / 2 };
    }
  });
}

/**
 * Distribute rects evenly along an axis between the first and last (by
 * position); with `spacing` set, pack them at that fixed gap from the first.
 * Returns NEW rects in the original order.
 */
export function computeDistribution(
  rects: Rect[],
  axis: "horizontal" | "vertical",
  spacing?: number
): Rect[] {
  if (rects.length <= 2 && spacing === undefined) return rects.map((r) => ({ ...r }));
  const key = axis === "horizontal" ? "left" : "top";
  const sizeKey = axis === "horizontal" ? "width" : "height";

  const indexed = rects.map((r, i) => ({ r, i }));
  indexed.sort((a, b) => a.r[key] - b.r[key]);

  const first = indexed[0].r;
  const positions: number[] = [];
  if (spacing !== undefined) {
    let cursor = first[key];
    for (const { r } of indexed) {
      positions.push(cursor);
      cursor += r[sizeKey] + spacing;
    }
  } else {
    const last = indexed[indexed.length - 1].r;
    const span = last[key] - first[key];
    const step = span / (indexed.length - 1);
    indexed.forEach((_, idx) => positions.push(first[key] + idx * step));
  }

  const out: Rect[] = new Array(rects.length);
  indexed.forEach(({ r, i }, idx) => {
    out[i] = { ...r, [key]: positions[idx] } as Rect;
  });
  return out;
}
